import { Container, Space, useMediaQuery } from '@ag1/ui';
import clsx from 'clsx';
import { useState } from 'react';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { UnifiedButtonType } from '../UnifiedButton';
import UnifiedButton from '../UnifiedButton/UnifiedButton';
import { CommonSectionProps } from '../types';
import TestimonialsContent from './TestimonialsContent/TestimonialsContent';
import TestimonialsFlyout from './TestimonialsFlyout/TestimonialsFlyout';
import TestimonialsHeading from './TestimonialsHeading/TestimonialsHeading';
import TestimonialsSliderThumbs from './TestimonialsSliderThumbs/TestimonialsSliderThumbs';
import { useTestimonialsDesktopSlider } from './hooks/useTestimonialsDesktopSlider';
import { useTestimonialsMobileSlider } from './hooks/useTestimonialsMobileSlider';
import styles from './styles.module.scss';
import { TestimonialProps } from './types';

const TestimonialsV5 = ({
  _type: sectionType,
  _key: key,
  subtitle,
  superHeadline,
  headline,
  isStyleVariant,
  bodyContent,
  cards,
  unifiedButton,
  textAlignment,
  bgColor,
  clickable,
  modalSuperHeadline,
  theme,
  addSlider,
  makeSquare,
  padding,
  wrapCards,
  visibleOnPageLoad,
  footnote
}: TestimonialProps & CommonSectionProps) => {
  const { ref, selectedSlide, handleThumbClick } = useTestimonialsMobileSlider();
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState({});

  const handleCloseFlyout = () => {
    setOpened(false);
  };

  const isClickable = !(clickable === undefined || clickable === false);
  const sliderDesktop = useTestimonialsDesktopSlider();

  const hasDesktopSlider = isClickable ? cards && cards.length > 6 : cards && cards.length > 3;

  const atSmall = useMediaQuery('s');

  const hasCards = cards && cards.length > 0;

  const showMobileWithSlider = (hasDesktopSlider || addSlider) && !atSmall;

  return (
    <div
      className={clsx(styles.root, bgColor === 'white' && styles.white, getThemeCSSClass(theme))}>
      <Container narrow id={key}>
        <article className={clsx(styles.content, getSectionPaddingClasses(styles, padding))}>
          {(subtitle || superHeadline || headline || bodyContent || hasDesktopSlider) && (
            <div
              className={clsx(
                styles.center,
                cards?.length === 2 && styles.twoCardsHeading,
                hasDesktopSlider && styles.leftDesktop,
                textAlignment?.alignLeft && styles.leftDesktop,
                textAlignment?.alignMobileLeft && styles.leftMobile
              )}>
              <TestimonialsHeading
                sliderDesktopActive={
                  !!(!wrapCards && cards && (clickable ? cards?.length > 6 : cards?.length > 3))
                }
                onScrollNext={sliderDesktop.scrollNext}
                onScrollPrev={sliderDesktop.scrollPrev}
                subtitle={subtitle}
                superHeadline={superHeadline}
                headline={headline}
                bodyContent={bodyContent}
                textAlignment={textAlignment}
                theme={theme}
                isStyleVariant={isStyleVariant}
                clickable={clickable}
              />
            </div>
          )}
          {hasCards && (
            <>
              {showMobileWithSlider && !clickable ? (
                <>
                  <TestimonialsSliderThumbs
                    cards={cards}
                    selectedSlide={selectedSlide}
                    onThumbClick={handleThumbClick}
                    theme={theme}
                    visibleOnPageLoad={visibleOnPageLoad}
                  />
                  <TestimonialsContent
                    ref={ref}
                    cards={cards}
                    textAlignment={textAlignment}
                    addSlider={addSlider}
                    subtitle={subtitle}
                    headline={headline}
                    theme={theme}
                    makeSquare={makeSquare}
                    clickable={isClickable}
                    visibleOnPageLoad={visibleOnPageLoad}
                    footnote={footnote}
                  />
                </>
              ) : (
                <TestimonialsContent
                  addSlider={addSlider}
                  ref={
                    !clickable && cards.length > 3 && atSmall && !wrapCards
                      ? sliderDesktop.ref
                      : undefined
                  }
                  cards={cards}
                  textAlignment={textAlignment}
                  subtitle={subtitle}
                  headline={headline}
                  theme={theme}
                  makeSquare={makeSquare}
                  setOpened={setOpened}
                  setSelected={setSelected}
                  clickable={isClickable}
                  wrapCards={wrapCards}
                  visibleOnPageLoad={visibleOnPageLoad}
                  footnote={footnote}
                />
              )}
              {opened && (
                <TestimonialsFlyout
                  opened={opened}
                  selected={selected}
                  setSelected={setSelected}
                  cards={cards}
                  modalSuperHeadline={modalSuperHeadline}
                  closeFlyout={handleCloseFlyout}
                  footnote={footnote}
                />
              )}
            </>
          )}

          {unifiedButton?.switchButton !== 'noButton' && (
            <Space
              top={{ xxs: 40, m: 48, l: 56 }}
              className={clsx(
                styles.buttonContainer,
                !!addSlider && styles.space,
                !makeSquare && styles.center
              )}>
              <UnifiedButton
                unifiedButton={unifiedButton as UnifiedButtonType}
                sectionType={sectionType || ''}
                className={styles.button}
                size="medium"
                theme={theme}
              />
            </Space>
          )}
        </article>
      </Container>
    </div>
  );
};

export default TestimonialsV5;
